import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { createTournament, startHand, applyAction, advanceRunout, viewFor } from "@/poker/engine";
import { aiDecide } from "@/poker/ai";
import { chen, estimateEquity } from "@/poker/equity";
import { buildSummary } from "@/poker/feedback";
import { describeModels } from "@/poker/opponentModel";
import { cardsText, fmt } from "@/poker/cards";

const clone = (s) => structuredClone(s);

function recordDecision(s, type, amount) {
  const v = viewFor(s, 0);
  const post = v.street !== "preflop";
  return {
    hand: s.handNum, street: v.street, type, amount, toCall: v.toCall, pot: v.pot, maxTo: v.maxTo,
    potOdds: v.toCall ? v.toCall / (v.pot + v.toCall) : 0,
    equity: post ? estimateEquity(v.hole, v.board, Math.max(1, v.nOpp), 500) : null,
    chen: chen(v.hole), pos: v.pos, effBB: v.effBB, raiseCount: v.raiseCount,
    hole: cardsText(v.hole), board: post ? cardsText(v.board) : "",
  };
}

export function useTournament() {
  const [game, setGame] = useState(null);
  const [summary, setSummary] = useState(null);
  const [preAction, setPreAction] = useState(null);
  const decisions = useRef([]);
  const gameRef = useRef(null);

  const commit = useCallback((s) => {
    gameRef.current = s; setGame(s);
    window.pokerModels = () => console.table(describeModels(s.model, s.players.map((p) => p.name)));
  }, []);

  const start = useCallback((cfg) => {
    decisions.current = [];
    const s = createTournament(cfg);
    startHand(s);
    setSummary(null);
    setPreAction(null);
    commit(s);
  }, [commit]);

  const nextHand = useCallback(() => {
    const g = gameRef.current;
    if (!g || g.phase !== "handover") return;
    setPreAction(null);
    if (g.gameOver) { setSummary(buildSummary(g, decisions.current)); return; }
    const s = clone(g);
    startHand(s);
    if (s.levelUp) toast(`Blinds up: ${fmt(s.sb)}/${fmt(s.bb)}`, { description: `Level ${s.level + 1}` });
    commit(s);
  }, [commit]);

  const act = useCallback((type, amount) => {
    const g = gameRef.current;
    if (!g || g.phase !== "betting" || g.toAct !== 0) return;
    const s = clone(g);
    decisions.current.push(recordDecision(s, type, amount));
    applyAction(s, 0, { type, amount });
    setPreAction(null);
    commit(s);
  }, [commit]);

  const quit = useCallback(() => { gameRef.current = null; setGame(null); setSummary(null); }, []);

  useEffect(() => {
    if (!game || summary) return undefined;
    let t;
    if (game.phase === "betting" && game.toAct > 0) {
      const me = game.players[0];
      const delay = !me.folded && !me.out ? 650 + Math.random() * 700 : 300;
      t = setTimeout(() => {
        const s = clone(game);
        const idx = s.toAct;
        applyAction(s, idx, aiDecide(viewFor(s, idx), s.players[idx].skill));
        commit(s);
      }, delay);
    } else if (game.phase === "runout") {
      t = setTimeout(() => { const s = clone(game); advanceRunout(s); commit(s); }, 1200);
    } else if (game.phase === "handover") {
      t = setTimeout(nextHand, game.gameOver ? 3000 : game.result?.showdown ? 4200 : 2200);
    }
    return () => clearTimeout(t);
  }, [game, summary, nextHand, commit]);

  useEffect(() => {
    if (game?.phase === "handover" && !game.gameOver && game.result?.pots.some((p) => p.winners.includes(0))) {
      toast.success("You won the pot!", { id: `win-${game.handNum}` });
    }
  }, [game]);

  useEffect(() => {
    if (!game || !preAction || game.phase !== "betting" || game.toAct !== 0) return;
    const v = viewFor(game, 0);
    if (preAction === "checkfold") act(v.toCall === 0 ? "check" : "fold");
    else if (preAction === "check") { if (v.toCall === 0) act("check"); else setPreAction(null); }
    else if (preAction === "callany") act(v.toCall === 0 ? "check" : "call");
  }, [game, preAction, act]);

  return { game, summary, start, act, nextHand, quit, preAction, setPreAction };
}
